import * as _ from 'lodash';
import {
  BatchDiagnostic,
  BatchItemOfCourierBatchItemValuesDto,
  BatchResultDto,
  CarrierHierarchyDto,
  CourierBatchImportDto,
  CourierBatchItemValuesDto,
  CourierBatchPrepareDto,
  CourierBatchPreparedDto,
  CourierDto,
  KnownBatchRowOutcome
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { resolveCourierDestination } from './courier-form.map';
import {
  ParsedCourierCsv,
  rowNumberOf,
  worstSeverity,
  INVALID_CARRIER_CODES,
  isRowLocked
} from './courier-batch.utils';

export const COURIER_BATCH_FIELDS = [
  'firstName',
  'lastName',
  'preferredName',
  'mobile',
  'externalID',
  'username',
  'accessCode',
  'image',
  'groupName',
  'groupID',
  'id'
] as const;

export type CourierBatchField = (typeof COURIER_BATCH_FIELDS)[number];

export const COURIER_BATCH_FIELD_LABELS: Record<CourierBatchField, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  preferredName: 'Preferred Name',
  mobile: 'Mobile',
  externalID: 'External ID',
  username: 'Username',
  accessCode: 'Access Code',
  image: 'Image URL',
  groupName: 'Carrier',
  groupID: 'Carrier ID',
  id: 'Courier ID'
};

/** The CSV template's headings, in order. */
export const COURIER_BATCH_TEMPLATE_FIELDS = [
  'firstName',
  'lastName',
  'preferredName',
  'mobile',
  'externalID',
  'username',
  'accessCode',
  'groupName',
  'groupID'
] as const;

export type CourierBatchValues = Partial<CourierBatchItemValuesDto>;

export interface CourierBatchAnchor {
  projectID: string;
  groupID?: string;
}

export function resolveBatchDefaultCarrierID(hierarchy?: CarrierHierarchyDto): string | undefined {
  return resolveCourierDestination(hierarchy).groupID ?? hierarchy?.organisation?.id;
}

export type CourierBatchRowStatus = 'ready' | 'warning' | 'error' | 'edited' | 'uploading' | 'uploaded' | 'failed';

export interface CourierBatchRow {
  rowID: string;
  sourceRow?: number;
  values: CourierBatchValues;
  action?: string;
  diagnostics: BatchDiagnostic[];
  status: CourierBatchRowStatus;
  result?: BatchResultDto;
  isSelected: boolean;
  resolvedCarrier?: CourierBatchResolvedCarrier;
}

export interface CourierBatchResolvedCarrier {
  id: string;
  name?: string;
}

export type CourierBatchSentCarriers = Map<string, Pick<CourierBatchValues, 'groupID' | 'groupName'>>;

/** The template the operator fills in: the field labels as headings, and two example couriers. */
export function makeCourierCsvTemplate(): string {
  const headings = COURIER_BATCH_TEMPLATE_FIELDS.map((field) => COURIER_BATCH_FIELD_LABELS[field]);
  const examples = [
    ['Kaye', 'Totleben', '', '+61400000000', 'DRV001', '', '', '', ''],
    ['Rani', 'Smith', 'Ran', '', '', 'rsmith', '0123', '', '']
  ];

  return [headings.join(','), ...examples.map((row) => row.join(','))].join('\n');
}

/**
 * A carrier's couriers in the template's shape, so the file can be edited and imported back. Each row
 * names the courier's own carrier, which may be a region beneath the one exported.
 */
export function makeCourierCsvExport(couriers: CourierDto[]): string {
  const headings = COURIER_BATCH_TEMPLATE_FIELDS.map((field) => COURIER_BATCH_FIELD_LABELS[field]);
  const rows = couriers.map((courier) => {
    const values: CourierBatchValues = {
      ...courier,
      groupName: courier.carrier?.name,
      groupID: courier.carrier?.id
    };
    return COURIER_BATCH_TEMPLATE_FIELDS.map((field) => values[field] ?? '');
  });

  return [headings, ...rows].map((cells) => cells.map(escapeCsvCell).join(',')).join('\n');
}

/** Quoted only when it has to be: a comma, quote or line break would otherwise split the cell. */
function escapeCsvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * The most rows one request carries. A larger batch goes as several requests, one after another.
 */
export const COURIER_BATCH_REQUEST_SIZE = 100;

export function makeCsvPrepareDtos(
  csv: ParsedCourierCsv,
  rowIDs: string[],
  anchor: CourierBatchAnchor
): CourierBatchPrepareDto[] {
  return _.range(0, csv.rows.length, COURIER_BATCH_REQUEST_SIZE).map((start) => {
    const end = start + COURIER_BATCH_REQUEST_SIZE;
    return {
      columns: csv.columns,
      rows: csv.rows.slice(start, end),
      rowIDs: rowIDs.slice(start, end),
      firstSourceRow: start + 1,
      ...anchor
    };
  });
}

export function makePrepareDtos(rows: CourierBatchRow[], anchor: CourierBatchAnchor): CourierBatchPrepareDto[] {
  const runs: CourierBatchRow[][] = [];
  let previousNumber: number | undefined;

  for (const row of rows.filter((candidate) => !isRowLocked(candidate))) {
    const number = rowNumberOf(row.rowID);
    const run = runs[runs.length - 1];
    const continuesRun = run && number !== undefined && previousNumber !== undefined && number === previousNumber + 1;
    if (continuesRun && run.length < COURIER_BATCH_REQUEST_SIZE) {
      run.push(row);
    } else {
      runs.push([row]);
    }
    previousNumber = number;
  }

  return runs.map((run) => {
    const firstSourceRow = rowNumberOf(run[0].rowID);
    return {
      columns: [...COURIER_BATCH_FIELDS],
      rows: run.map((row) => COURIER_BATCH_FIELDS.map((field) => row.values[field] ?? '')),
      rowIDs: run.map((row) => row.rowID),
      ...(firstSourceRow === undefined ? {} : { firstSourceRow }),
      ...anchor
    };
  });
}

export function makeImportDtos(
  rows: CourierBatchRow[],
  rowIDs: string[],
  anchor: CourierBatchAnchor
): CourierBatchImportDto[] {
  return _.chunk(rowIDs, COURIER_BATCH_REQUEST_SIZE).map((requestRowIDs) => {
    const sending = new Set(requestRowIDs);
    return {
      ...anchor,
      items: rows
        .filter((row) => sending.has(row.rowID))
        .map((row) => {
          const values: CourierBatchValues = Object.fromEntries(
            COURIER_BATCH_FIELDS.map((field) => [field, row.values[field]?.trim()]).filter(([, value]) => value)
          );
          return { rowID: row.rowID, values: { ...values, firstName: values.firstName ?? '' } };
        })
    };
  });
}

/**
 * The rows of an upload that were asked for, in the order asked. A prepare sent under an `uploadID`
 * answers with every row of the upload, so this narrows it to the rows a request has settled —
 * and puts them back in the file's order, which the response doesn't promise to keep.
 */
export function pickPreparedRows(prepared: CourierBatchPreparedDto, rowIDs: string[]): CourierBatchPreparedDto {
  const itemsByRow = new Map(prepared.items.map((item) => [item.rowID, item]));
  return { ...prepared, items: rowIDs.flatMap((rowID) => itemsByRow.get(rowID) ?? []) };
}

export function mapPreparedToRows(
  prepared: CourierBatchPreparedDto,
  previousRows?: CourierBatchRow[],
  firstSentCarriers?: CourierBatchSentCarriers
): CourierBatchRow[] {
  const diagnosticsByRow = _.groupBy(
    prepared.diagnostics.filter((diagnostic) => diagnostic.rowID),
    (diagnostic) => diagnostic.rowID
  );
  const toRow = (
    item: BatchItemOfCourierBatchItemValuesDto,
    sent: Pick<CourierBatchValues, 'groupID' | 'groupName'> | undefined,
    previous?: CourierBatchRow
  ): CourierBatchRow => {
    const diagnostics = diagnosticsByRow[item.rowID] ?? [];
    const status: CourierBatchRowStatus = worstSeverity(diagnostics) ?? 'ready';
    // The API writes the carrier it routed to into the values. That's kept apart, so the row goes on
    // sending only the carrier it names — the default stays the request's `groupID`.
    const { groupID, groupName, ...values } = (item.values ?? {}) as CourierBatchValues;
    const isRouted = !!groupID && !diagnostics.some((diagnostic) => INVALID_CARRIER_CODES.includes(diagnostic.code));
    return {
      rowID: item.rowID,
      sourceRow: item.sourceRow,
      values: _.omitBy({ ...values, groupID: sent?.groupID, groupName: sent?.groupName }, _.isUndefined),
      action: item.action,
      diagnostics,
      status,
      // Rows without errors start ticked, and rows with errors can't be ticked. On a later prepare a
      // row keeps the operator's choice, except that one just out of error is ticked again.
      isSelected: status !== 'error' && (!previous || previous.status === 'error' || previous.isSelected),
      resolvedCarrier: isRouted ? { id: groupID!, name: groupName } : undefined
    };
  };

  if (!previousRows) {
    return prepared.items.map((item) => toRow(item, firstSentCarriers?.get(item.rowID)));
  }

  const itemsByRow = new Map(prepared.items.map((item) => [item.rowID, item]));

  return previousRows.map((previous) => {
    const item = itemsByRow.get(previous.rowID);
    if (previous.status === 'uploaded' || !item) {
      return previous;
    }
    const sent = { groupID: previous.values.groupID, groupName: previous.values.groupName };
    return { ...toRow(item, sent, previous), sourceRow: previous.sourceRow, result: previous.result };
  });
}

/** Diagnostics about the whole batch rather than a row — a carrier that couldn't be loaded, say. */
export function batchLevelDiagnostics(prepared: Pick<CourierBatchPreparedDto, 'diagnostics'>): BatchDiagnostic[] {
  return prepared.diagnostics.filter((diagnostic) => !diagnostic.rowID);
}

export function csvSentCarriers(
  csv: ParsedCourierCsv,
  rowIDs: string[],
  mapping: CourierBatchPreparedDto['mapping']
): CourierBatchSentCarriers {
  const columnOf = (field: CourierBatchField) => {
    const heading = mapping.find((entry) => entry.field === field)?.from;
    return heading === undefined ? -1 : csv.columns.indexOf(heading);
  };
  const idColumn = columnOf('groupID');
  const nameColumn = columnOf('groupName');
  const cell = (cells: string[], column: number) => (column >= 0 ? cells[column]?.trim() || undefined : undefined);

  return new Map(
    csv.rows.map((cells, index) => [
      rowIDs[index],
      _.omitBy({ groupID: cell(cells, idColumn), groupName: cell(cells, nameColumn) }, _.isUndefined)
    ])
  );
}

export function applyImportResults(rows: CourierBatchRow[], results: BatchResultDto[]): CourierBatchRow[] {
  const resultsByRow = new Map(results.map((result) => [result.rowID, result]));

  return rows.map((row) => {
    const result = resultsByRow.get(row.rowID);

    const isCommitted = [KnownBatchRowOutcome.Added, KnownBatchRowOutcome.Updated, KnownBatchRowOutcome.NoChange].some(
      (outcome) => outcome === result?.outcome
    );

    if (result && isCommitted) {
      return { ...row, status: 'uploaded', result, isSelected: false };
    }

    if (result) {
      return { ...row, status: 'failed', result, isSelected: true };
    }

    return row;
  });
}
